if (!customElements.get('ugc-feed')) {
  class UgcFeed extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('[data-ugc-viewer]');
      if (!this.dialog) return;

      this.list = this.dialog.querySelector('[data-ugc-list]');
      this.template = this.querySelector('template[data-ugc-slides]');
      this.live = this.dialog.querySelector('[data-ugc-live]');
      this.counter = this.dialog.querySelector('[data-ugc-counter]');
      this.btn = {
        play: this.dialog.querySelector('[data-ugc-play]'),
        playLabel: this.dialog.querySelector('[data-ugc-play-label]'),
        mute: this.dialog.querySelector('[data-ugc-mute]'),
        cc: this.dialog.querySelector('[data-ugc-cc]'),
        prev: this.dialog.querySelector('[data-ugc-prev]'),
        next: this.dialog.querySelector('[data-ugc-next]')
      };
      this.rail = this.querySelector('[data-ugc-rail]');
      this.railNav = this.querySelector('[data-ugc-rail-nav]');
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');

      this.slides = [];
      this.index = 0;
      this.hasActive = false;
      this.muted = true;
      this.userPaused = false;
      this.captionsOn = true;
      this.pushedHistory = false;
      this.opener = null;

      this.onClick = this.onClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onClose = this.onClose.bind(this);
      this.onPopState = this.onPopState.bind(this);

      this.addEventListener('click', this.onClick);
      this.dialog.addEventListener('keydown', this.onKeydown);
      this.dialog.addEventListener('close', this.onClose);

      this.setupRailNav();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      window.removeEventListener('popstate', this.onPopState);
      this.observer?.disconnect();
      this.railResize?.disconnect();
    }

    get scrollBehavior() {
      return this.motion.matches ? 'auto' : 'smooth';
    }

    get currentVideo() {
      return this.slides[this.index]?.querySelector('video') || null;
    }

    onClick(event) {
      var button = event.target.closest('button');
      if (!button || !this.contains(button)) return;
      if (button.getAttribute('aria-disabled') === 'true') return;

      if (button.hasAttribute('data-ugc-open')) return this.open(Number(button.dataset.index), button);
      if (button.hasAttribute('data-ugc-close')) return this.dialog.close();
      if (button === this.btn.play) return this.togglePlay();
      if (button === this.btn.mute) return this.toggleMute();
      if (button === this.btn.cc) return this.toggleCaptions();
      if (button === this.btn.prev) return this.goTo(this.index - 1);
      if (button === this.btn.next) return this.goTo(this.index + 1);
      if (button.hasAttribute('data-ugc-rail-prev')) return this.scrollRail(-1);
      if (button.hasAttribute('data-ugc-rail-next')) return this.scrollRail(1);
    }

    onKeydown(event) {
      if (event.target.closest('[data-ugc-info]')) return;
      var keys = {
        ArrowDown: 1, PageDown: 1, ArrowUp: -1, PageUp: -1,
        Home: -Infinity, End: Infinity
      };
      if (!(event.key in keys)) return;
      event.preventDefault();
      var step = keys[event.key];
      var target = Number.isFinite(step)
        ? this.index + step
        : (step < 0 ? 0 : this.slides.length - 1);
      this.goTo(target);
    }

    renderSlides() {
      if (this.slides.length) return;
      this.list.append(this.template.content.cloneNode(true));
      this.slides = Array.from(this.list.querySelectorAll('[data-ugc-slide]'));

      this.observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
            this.setActive(this.slides.indexOf(entry.target));
          }
        }.bind(this));
      }.bind(this), { root: this.list, threshold: 0.6 });

      this.slides.forEach(function (slide) {
        this.observer.observe(slide);
        var video = slide.querySelector('video');
        if (video) {
          video.addEventListener('play', function () { this.updateControls(); }.bind(this));
          video.addEventListener('pause', function () { this.updateControls(); }.bind(this));
        }
      }.bind(this));
    }

    open(index, opener) {
      this.renderSlides();
      var slide = this.slides[index];
      if (!slide) return;

      this.opener = opener;
      this.hasActive = false;
      this.dialog.showModal();
      this.list.scrollTo({ top: slide.offsetTop, behavior: 'instant' });
      this.setActive(index, false);
      slide.focus({ preventScroll: true });

      if (!window.Shopify?.designMode) {
        history.pushState({ ugcViewer: true }, '');
        this.pushedHistory = true;
        window.addEventListener('popstate', this.onPopState);
      }
    }

    onPopState() {
      this.pushedHistory = false;
      if (this.dialog.open) this.dialog.close();
    }

    onClose() {
      this.slides.forEach(function (slide) {
        var video = slide.querySelector('video');
        if (video) video.pause();
      });
      window.removeEventListener('popstate', this.onPopState);
      if (this.pushedHistory) {
        this.pushedHistory = false;
        history.back();
      }
      this.opener?.focus();
    }

    goTo(index) {
      if (index < 0 || index >= this.slides.length) return;
      this.slides[index].scrollIntoView({ block: 'start', behavior: this.scrollBehavior });
    }

    setActive(index, announce) {
      if (announce === undefined) announce = true;
      if (index < 0 || index >= this.slides.length) return;
      var changed = index !== this.index || !this.hasActive;
      this.index = index;
      this.hasActive = true;

      this.slides.forEach(function (slide, i) {
        slide.toggleAttribute('data-active', i === index);
        var video = slide.querySelector('video');
        if (video && i !== index && !video.paused) video.pause();
      });

      this.maybeAutoplay();
      this.updateControls();

      if (changed && announce) {
        this.live.textContent = this.slides[index].dataset.announce;
      }
    }

    maybeAutoplay() {
      var video = this.currentVideo;
      if (!video || !this.dialog.open) return;
      video.muted = this.muted;
      this.applyCaptions(video);
      if (this.motion.matches || this.userPaused) return;
      if (video.paused) video.play().catch(function () { this.updateControls(); }.bind(this));
    }

    togglePlay() {
      var video = this.currentVideo;
      if (!video) return;
      if (video.paused) {
        this.userPaused = false;
        video.muted = this.muted;
        video.play().catch(function () { this.updateControls(); }.bind(this));
      } else {
        this.userPaused = true;
        video.pause();
      }
    }

    toggleMute() {
      this.muted = !this.muted;
      var video = this.currentVideo;
      if (video) video.muted = this.muted;
      this.updateControls();
    }

    toggleCaptions() {
      this.captionsOn = !this.captionsOn;
      var video = this.currentVideo;
      if (video) this.applyCaptions(video);
      this.updateControls();
    }

    applyCaptions(video) {
      Array.from(video.textTracks).forEach(function (track) {
        if (track.kind === 'captions') track.mode = this.captionsOn ? 'showing' : 'hidden';
      }.bind(this));
    }

    updateControls() {
      var video = this.currentVideo;
      var isVideo = Boolean(video);
      var hasCaptions = isVideo && Boolean(video.querySelector('track[kind="captions"]'));

      this.btn.play.hidden = !isVideo;
      this.btn.mute.hidden = !isVideo;
      this.btn.cc.hidden = !hasCaptions;

      if (isVideo) {
        var playing = !video.paused;
        this.btn.play.dataset.state = playing ? 'playing' : 'paused';
        this.btn.playLabel.textContent = playing ? 'Pause video' : 'Play video';
        this.btn.mute.setAttribute('aria-pressed', String(this.muted));
        this.btn.cc.setAttribute('aria-pressed', String(this.captionsOn));
      }

      this.btn.prev.setAttribute('aria-disabled', String(this.index === 0));
      this.btn.next.setAttribute('aria-disabled', String(this.index === this.slides.length - 1));
      this.counter.textContent = (this.index + 1) + ' / ' + this.slides.length;
    }

    setupRailNav() {
      if (!this.rail || !this.railNav) return;
      this.railPrev = this.railNav.querySelector('[data-ugc-rail-prev]');
      this.railNext = this.railNav.querySelector('[data-ugc-rail-next]');
      var self = this;
      var update = function () {
        var max = self.rail.scrollWidth - self.rail.clientWidth;
        self.railNav.hidden = max <= 1;
        self.railPrev.setAttribute('aria-disabled', String(self.rail.scrollLeft <= 1));
        self.railNext.setAttribute('aria-disabled', String(self.rail.scrollLeft >= max - 1));
      };
      this.rail.addEventListener('scroll', update, { passive: true });
      this.railResize = new ResizeObserver(update);
      this.railResize.observe(this.rail);
      update();
    }

    scrollRail(direction) {
      this.rail.scrollBy({ left: direction * this.rail.clientWidth * 0.8, behavior: this.scrollBehavior });
    }
  }

  customElements.define('ugc-feed', UgcFeed);
}
